import { CentralizedUrlAdapter } from '../adapters/CentralizedUrlAdapter';
import { OverduePaymentButtonController, OverdueSummaryController } from '../controllers';
import { initializerHelpers, type Listeners } from '../helpers';
import {
  PaymentMethodsManager,
  BillingInfoFormManager,
  UpdateBillingManager,
  AlertManager,
  OverduePaymentButtonManager,
  OverdueSummaryManager,
  RouterNonUiManager,
} from '../managers';

export class OverdueBalanceInitializer {
  public listeners: Listeners;

  // managers
  private billingInfoForm: BillingInfoFormManager;

  private paymentMethods: PaymentMethodsManager;

  private updateBilling: UpdateBillingManager;

  private alert: AlertManager;

  private router: RouterNonUiManager;

  private overdueSummary: OverdueSummaryManager;

  private overduePaymentButton: OverduePaymentButtonManager;

  // init methods
  public billingInfoFormInit: BillingInfoFormManager['billingInfoFormInit'];

  public paymentMethodsInit: PaymentMethodsManager['paymentMethodsInit'];

  public updateBillingInit: UpdateBillingManager['updateBillingInit'];

  public alertInit: AlertManager['alertInit'];

  public routerNonUiInit: RouterNonUiManager['routerNonUiInit'];

  public overdueSummaryInit: OverdueSummaryManager['overdueSummaryInit'];

  public overduePaymentButtonInit: OverduePaymentButtonManager['overduePaymentButtonInit'];

  constructor() {
    const urlAdapter = new CentralizedUrlAdapter();

    this.billingInfoForm = new BillingInfoFormManager();
    this.paymentMethods = new PaymentMethodsManager(urlAdapter);
    this.updateBilling = new UpdateBillingManager();
    this.alert = new AlertManager();
    this.router = new RouterNonUiManager();
    this.overdueSummary = new OverdueSummaryManager();
    this.overduePaymentButton = new OverduePaymentButtonManager();

    this.billingInfoFormInit = this.billingInfoForm.billingInfoFormInit;
    this.paymentMethodsInit = this.paymentMethods.paymentMethodsInit;
    this.updateBillingInit = this.updateBilling.updateBillingInit;
    this.alertInit = this.alert.alertInit;
    this.routerNonUiInit = this.router.routerNonUiInit;
    this.overdueSummaryInit = this.overdueSummary.overdueSummaryInit;
    this.overduePaymentButtonInit = this.overduePaymentButton.overduePaymentButtonInit;

    this.redirectIfHasPositiveBalance(this.overdueSummary, this.router);

    this.setWarningAlert(this.paymentMethods);

    this.listeners = this.initializeControllers();
  }

  private initializeControllers() {
    const controllers = [
      new OverdueSummaryController(this.overdueSummary, this.paymentMethods),
      new OverduePaymentButtonController(
        this.paymentMethods,
        this.billingInfoForm,
        this.router,
        this.overduePaymentButton,
        this.overdueSummary,
        this.alert,
        this.updateBilling
      ),
    ];

    initializerHelpers.initControllers(controllers);

    return initializerHelpers.createListeners(controllers);
  }

  private async setWarningAlert(paymentMethodsManager: PaymentMethodsManager) {
    const api = await paymentMethodsManager.getApiWhenReady();

    api.setCurrentPaymentMethodWarning();
  }

  private async redirectIfHasPositiveBalance(
    overdueSummaryManager: OverdueSummaryManager,
    routerManager: RouterNonUiManager
  ) {
    const api = await overdueSummaryManager.getApiWhenReady();

    // if there is no overdue balance, redirect to regular billing info page
    if (api.isBalanceOverdueFree()) {
      routerManager.getApiWhenReady().then((routerApi) => {
        routerApi.goToBillingInfo({ replace: true, keepParams: true });
      });
    }
  }
}
